'use client';

import type { CSSProperties, ReactNode } from 'react';
import type { RenderDescriptor } from '../../vendor/template-system/v2/runtime/renderer/types';
import { HtmlDescriptorAdapter } from './HtmlDescriptorAdapter';
import {
  PlaceholderChromeOverlay,
  placeholderDashedBorderCss,
  PLACEHOLDER_UNLINKED_OPACITY,
} from './PlaceholderChrome';
import type { PaintContext } from './viewingPaintHelpers';
import {
  dropShadowToBoxShadow,
  resolveDefaultDropShadow,
} from './defaultDropShadow';

export interface ItemCardHostProps {
  width: number;
  height: number;
  borderRadius?: number;
  frameThickness?: number;
  backgroundColor?: string;
  borderColor?: string;
  descriptors: RenderDescriptor[];
  /** Sparse Containd `defaultDropShadow` JSON for the item shell. */
  defaultDropShadow?: string | null;
  /** Fallback when drop-shadow JSON omits color (share theme `itemDropShadow`). */
  dropShadowFallbackColor?: string | null;
  onClick?: () => void;
  onDoubleClick?: () => void;
  overlay?: ReactNode;
  /** DEC-062 placeholder seat chrome (custom placeholder layout bake). */
  isPlaceholder?: boolean;
  /** False when the original is not in this freeze (muted chrome, no jump). */
  placeholderLinked?: boolean;
  /** Optional host theme injection forwarded to HtmlDescriptorAdapter. */
  paintContext?: PaintContext;
}

/** Outer chrome matching TemplateObjectRenderer host around V2DescriptorAdapter. */
export function ItemCardHost({
  width,
  height,
  borderRadius = 8,
  frameThickness = 2,
  backgroundColor = '#ffffff',
  borderColor = '#cccccc',
  descriptors,
  defaultDropShadow,
  dropShadowFallbackColor,
  onClick,
  onDoubleClick,
  overlay,
  isPlaceholder = false,
  placeholderLinked = true,
  paintContext,
}: ItemCardHostProps) {
  const dashed = isPlaceholder
    ? placeholderDashedBorderCss(frameThickness, borderColor, placeholderLinked)
    : null;
  const dropShadow = resolveDefaultDropShadow(defaultDropShadow, {
    fallbackColor: dropShadowFallbackColor ?? undefined,
  });
  const boxShadow = dropShadowToBoxShadow(dropShadow);
  const shellStyle: CSSProperties = {
    width,
    height,
    borderRadius,
    borderWidth: dashed?.borderWidth ?? frameThickness,
    borderStyle: dashed?.borderStyle ?? 'solid',
    borderColor: dashed?.borderColor ?? borderColor,
    backgroundColor,
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'relative',
    cursor: onClick || onDoubleClick ? 'pointer' : 'default',
    opacity: isPlaceholder && !placeholderLinked ? PLACEHOLDER_UNLINKED_OPACITY : undefined,
  };

  return (
    <div
      style={{
        width,
        height,
        borderRadius,
        boxShadow,
        // Keep shadow outside the clipped shell (GenericItemHost parity).
        overflow: 'visible',
      }}
    >
    <div
      style={shellStyle}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      role={onClick ? 'button' : undefined}
      data-template-item
      data-placeholder={isPlaceholder ? '1' : undefined}
      data-placeholder-unlinked={
        isPlaceholder && !placeholderLinked ? '1' : undefined
      }
    >
      <HtmlDescriptorAdapter descriptors={descriptors} paintContext={paintContext} />
      {isPlaceholder ? (
        <PlaceholderChromeOverlay linked={placeholderLinked} />
      ) : null}
      {overlay}
    </div>
    </div>
  );
}
